import { useContext, useEffect, useState } from 'react'
import { get } from 'nested-property'
import { useQuery } from '@apollo/client'
import { OrgContext } from 'ui/contexts'
import useIsMounted from 'utilities/hooks/isMounted'
import { GET_USERS } from 'domains/carrotGraph/queries'

const getCursor = (data) => get(data, 'org.membershipList.pageInfo.endCursor')

/**
 * Request function that pulls memberships info and formats and returns user data
 *
 * @param {object} data the existing user data
 * @returns {Array} an array of users
 */
const getUsers = function (data) {
  const memberships = get(data, 'org.membershipList.memberships')
  const users = memberships?.map(
    ({ id, status, admin, user, role, disabled }) => ({
      ...user,
      status,
      admin,
      membershipId: id,
      membershipRole: role,
      disabled,
    }),
  )
  return users
}

/**
 * Get a list of Users for the default organization
 * @return {Object} includes loading, data, error, and fetchMore from graphQL query
 */
const useUsers = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [sortedUsers, setSortedUsers] = useState([])
  const { id: orgId } = useContext(OrgContext)
  const isMounted = useIsMounted()

  const { loading, data, error, fetchMore } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-and-network',
    variables: { orgId, withDisabled: true },
    errorPolicy: 'all',
  })

  useEffect(() => {
    const cursor = getCursor(data)

    if (isMounted && !cursor && !loading) {
      const users = getUsers(data)

      const sortedUsers = users?.sort((a, b) =>
        (a.displayName || a.email).toLowerCase() >
        (b.displayName || b.email).toLowerCase()
          ? 1
          : -1,
      )

      setSortedUsers(sortedUsers)
      setIsLoading(false)
      return
    }
  }, [data, isMounted, loading])

  useEffect(() => {
    const cursor = getCursor(data)

    if (!cursor) {
      return
    }

    fetchMore({
      variables: { cursor },
      updateQuery(previousResult, { fetchMoreResult }) {
        const prevMemberships =
          previousResult.org.membershipList.memberships || []
        const newMemberships =
          fetchMoreResult.org.membershipList.memberships || []

        return {
          ...fetchMoreResult,
          org: {
            ...(fetchMoreResult.org || {}),
            membershipList: {
              ...(fetchMoreResult.org.membershipList || {}),
              memberships: [...prevMemberships, ...newMemberships],
            },
          },
        }
      },
    })
  }, [data, fetchMore])

  return {
    loading: isLoading,
    data: sortedUsers,
    error,
    fetchMore,
  }
}

export default useUsers
